import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {trackOpenedRelevantTemplate} from 'nav/analytics/RelevantTemplatesAnalytics';
import {Organization} from 'orgs/OrganizationConstants';
import EntityStore from 'stores/EntityStore';
import {GRAY_DARKER} from 'ui-library/Colors';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {readableTime} from 'util/DateUtil';

import {RelevantTemplatesTableQuery} from './__generated/RelevantTemplatesTableQuery';
import {NO_RELEVANT_TEMPLATES_STRING, HEADER_COLUMNS} from './RelevantTemplatesConstants';

type RelevantTemplateNode =
  RelevantTemplatesTableQuery['relevantTemplatesListing']['relevantTemplates'][number];
type RelevantTable = RelevantTemplateNode['relevantTables'][number];

type Subtemplate = ExtractByTypename<RelevantTemplateNode['template'], 'SubtemplateNode'>;

type Template = ExtractByTypename<RelevantTemplateNode['template'], 'TemplateNode'>;

export const RELEVANT_TEMPLATES_TABLE_FRAGMENT = gql`
  fragment RelevantTemplatesTableFragment on RelevantTemplatesListingNode {
    totalPostFiltering
    relevantTemplates {
      template {
        ... on TemplateNode {
          __typename
          id
          name
          archiveRecord {
            purpose
          }
          editUrl
          modifiedAt
          templateCollection {
            id
            name
          }
        }
        ... on SubtemplateNode {
          __typename
          id
          name
          archiveRecord {
            purpose
          }
          editUrl
          modifiedAt
          templateCollection {
            id
            name
          }
        }
      }
      relevantTables {
        id
        name
      }
    }
  }
`;

interface TableNamesDisplayProps {
  relevantTables: Array<RelevantTable>;
}

const TEMPLATE_TYPE_TO_COL_NAME = new Map<string, string>([
  ['TemplateNode', 'Template'],
  ['SubtemplateNode', 'Sub-Template'],
]);

function TableNamesDisplay({relevantTables}: TableNamesDisplayProps): JSX.Element {
  return (
    <Cell className={css(styles.tableCell, styles.relevantTablesCell)}>
      {relevantTables.map((relevantTable) => (
        <div key={relevantTable.id}>{relevantTable.name}</div>
      ))}
    </Cell>
  );
}

interface TemplateNameDisplayProps {
  schemaId: string;
  schemaType: ModelTypeConstants;
  template: Template | Subtemplate;
}

function TemplateNameDisplay({schemaId, schemaType, template}: TemplateNameDisplayProps): JSX.Element {
  const {editUrl, name} = template;

  return (
    <Cell className={css(styles.tableCell)}>
      <a
        href={editUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          trackOpenedRelevantTemplate({
            schemaId,
            schemaType,
            isAdmin: EntityStore.currentUser().organizations.some((org: Organization) => org.admin),
          });
        }}
      >
        {name}
      </a>
    </Cell>
  );
}

export interface RelevantTemplatesTableProps {
  schemaId: string;
  schemaType: ModelTypeConstants;
  relevantTemplates: Array<RelevantTemplateNode>;
}

export default function RelevantTemplatesTable({
  schemaId,
  schemaType,
  relevantTemplates,
}: RelevantTemplatesTableProps): JSX.Element {
  return (
    <Table emptyTableText={NO_RELEVANT_TEMPLATES_STRING} numRows={relevantTemplates.length}>
      <Column
        header={
          <Cell className={css(styles.tableCell, styles.templateCell)}>{HEADER_COLUMNS.template.name}</Cell>
        }
        renderCell={(rowIndex) => (
          <TemplateNameDisplay
            template={relevantTemplates[rowIndex].template}
            schemaId={schemaId}
            schemaType={schemaType}
          />
        )}
      />
      <Column
        header={HEADER_COLUMNS.type.name}
        renderCell={(rowIndex) => (
          <Cell className={css(styles.tableCell, styles.typeCell)}>
            {TEMPLATE_TYPE_TO_COL_NAME.get(relevantTemplates[rowIndex].template.__typename)}
          </Cell>
        )}
      />
      <Column
        header={HEADER_COLUMNS.templateCollection.name}
        renderCell={(rowIndex) => (
          <Cell className={css(styles.tableCell, styles.collectionCell)}>
            {relevantTemplates[rowIndex].template.templateCollection.name}
          </Cell>
        )}
      />
      <Column
        header={HEADER_COLUMNS.modifiedAt.name}
        renderCell={(rowIndex) => (
          <Cell className={css(styles.tableCell, styles.lastModifiedCell)}>
            {readableTime(relevantTemplates[rowIndex].template.modifiedAt)}
          </Cell>
        )}
      />
      <Column
        header={HEADER_COLUMNS.relevantTables.name}
        renderCell={(rowIndex) => (
          <TableNamesDisplay relevantTables={relevantTemplates[rowIndex].relevantTables} />
        )}
      />
    </Table>
  );
}

const styles = StyleSheet.create({
  tableCell: {
    color: GRAY_DARKER,
    verticalAlign: 'top',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  templateCell: {
    width: '30%',
  },
  typeCell: {
    width: '25%',
  },
  collectionCell: {
    width: '25%',
  },
  lastModifiedCell: {
    width: '20%',
  },
  relevantTablesCell: {
    width: 'auto',
    display: 'grid',
    gridGap: '12px',
  },
});
